import React, { useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { css } from 'src/ui/style/css'
import { useTheme } from 'src/ui/style/useTheme'
import { View } from 'src/ui/basic/View'
import ChatInput from 'src/ui/chatbot/components/ChatInput'
import ChatMessages, {
    Message,
    MessageMetaItem
} from 'src/ui/chatbot/components/ChatMessages'
import { useTran } from 'src/i18n/useTran'
import { msg } from 'src/i18n/texts/keys'
import { useLocale, countryToLocale, getLanguageName } from 'src/i18n/locale'
import { getAppContext } from 'src/context/AppContext'
import { messagesSource } from 'src/i18n/texts'
import { isMobileScreen, ScreenSize } from 'src/ui/style/ScreenSize'
import { useChatbot } from 'src/ui/chatbot/ChatbotProvider'
import DeskClearedNotice from 'src/ui/chatbot/components/DeskClearedNotice'
import DeskIdleWarningNotice from 'src/ui/chatbot/components/DeskIdleWarningNotice'
import DeskErrorNotice from 'src/ui/chatbot/components/DeskErrorNotice'
// import { isIosNativeApp, isMobileWeb } from '../../geo/locationService'
// import {
//     useAndroidScrollLock,
//     useBodyScrollFreeze
// } from 'src/hooks/useBodyScrollLock'

const { Sizes } = ScreenSize

const PREDEFINED_TOPIC_KEYS: string[] = [
    msg.chatbot.chat.topic.selfStorage,
    msg.chatbot.chat.topic.shipmentPickupOrSending
]

function pickRandom<T>(arr: T[], n: number): T[] {
    const shuffled = [...arr].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, n)
}

interface ChatProps {
    onSendMessage?: (message: string) => Promise<string>
}

const Chat: React.FC<ChatProps> = ({ onSendMessage }) => {
    const t = useTran()
    const th = useTheme()
    const locale = useLocale()
    const [messages, setMessages] = useState<Message[]>([])
    const [loading, setLoading] = useState(false)
    const isMobile = isMobileScreen()
    const chatMessagesRef = useRef<HTMLDivElement>(null)
    const appContext = getAppContext()

    const {
        isOpen,
        sendMessageStream,
        abortStreaming,
        isStreaming,
        inactivity,
        systemBanner,
        clearWarning,
        markActivity,
        isInputLocked,
        dismissWarning
    } = useChatbot()

    const showInlineBanner = !isMobile && isOpen

    useEffect(() => () => abortStreaming(), [abortStreaming])

    useEffect(() => {
        if (inactivity.cleared) {
            setMessages([])
        }
    }, [inactivity.cleared])

    // useEffect(() => {
    //     clearWarning()
    // }, [markActivity, clearWarning])

    useEffect(() => {
        if (
            systemBanner?.type === 'error' ||
            systemBanner?.type === 'cleared' ||
            inactivity.cleared
        ) {
            setLoading(false)
            return
        }
        if (messages.length > 0) return

        setLoading(true)
        const timeoutId = window.setTimeout(() => {
            const domainLocale =
                countryToLocale[
                    appContext.config
                        .boxylandCountry as keyof typeof countryToLocale
                ] || 'en'

            const topicKeys = pickRandom(
                PREDEFINED_TOPIC_KEYS,
                Math.min(3, PREDEFINED_TOPIC_KEYS.length)
            )
            const metaItems: MessageMetaItem[] = topicKeys.map(key => ({
                value: t(key),
                originalTopicName:
                    messagesSource[domainLocale]?.[key] || t(key),
                lang: getLanguageName(locale)
            }))

            const welcomeMessage: Message = {
                id: uuidv4(),
                text: t(msg.chatbot.chat.startConversation),
                sender: 'bot',
                timestamp: new Date(),
                metaItems
            }
            setMessages(prev => (prev.length > 0 ? prev : [welcomeMessage]))
            setLoading(false)
        }, 500)

        return () => window.clearTimeout(timeoutId)
    }, [
        appContext.config.boxylandCountry,
        inactivity.cleared,
        locale,
        messages.length,
        systemBanner?.type,
        t
    ])

    const defaultOnSendMessage = async (message: string): Promise<string> => {
        return `This is a mock response to: "${message}". Please provide a real onSendMessage handler for production use.`
    }

    const upsertBotMessage = (
        botId: string,
        text: string,
        metaItems?: MessageMetaItem[]
    ) => {
        setMessages(prev => {
            const existingMsg = prev.find(m => m.id === botId)
            if (existingMsg) {
                return prev.map(m =>
                    m.id === botId
                        ? {
                              ...m,
                              text,
                              ...(metaItems?.length ? { metaItems } : {})
                          }
                        : m
                )
            } else {
                const botMsg: Message = {
                    id: botId,
                    text,
                    sender: 'bot',
                    timestamp: new Date(),
                    ...(metaItems?.length ? { metaItems } : {})
                }
                return [...prev, botMsg]
            }
        })
    }

    const handleStreamError = (botId: string, error: any) => {
        if (error?.message === 'aborted') return
        upsertBotMessage(botId, t(msg.chatbot.chat.error))
    }

    const normalize = (s = '') =>
        s
            .replace(/\r/g, '')
            .replace(/\u00A0/g, ' ') // NBSP -> space
            .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width
            .trim()

    const clearMetaChoices = (list: Message[]) => {
        let hasMeta = false
        for (const msg of list) {
            if (msg.metaItems && msg.metaItems.length > 0) {
                hasMeta = true
                break
            }
        }
        if (!hasMeta) return list
        return list.map(msg =>
            msg.metaItems ? { ...msg, metaItems: undefined } : msg
        )
    }

    const extractMetaItems = (
        meta: Record<string, unknown> | undefined
    ): MessageMetaItem[] => {
        if (!meta || typeof meta !== 'object') return []
        const rawLang = typeof meta.lang === 'string' ? meta.lang : undefined
        const lang = rawLang ? getLanguageName(rawLang) : undefined
        const translations =
            meta.translations && typeof meta.translations === 'object'
                ? (meta.translations as Record<string, unknown>)
                : meta
        const depths =
            meta.depths && typeof meta.depths === 'object'
                ? (meta.depths as Record<string, unknown>)
                : {}

        const parseDepth = (raw: unknown): number | undefined => {
            if (typeof raw === 'number' && Number.isInteger(raw)) return raw
            if (typeof raw === 'string') {
                const normalized = raw.trim()
                // Accept only integer strings (optional leading minus sign).
                if (!/^-?\d+$/.test(normalized)) return undefined
                const parsed = Number(normalized)
                if (Number.isInteger(parsed)) return parsed
            }
            return undefined
        }

        return Object.entries(translations)
            .filter(
                ([key]) =>
                    key !== 'lang' && key !== 'translations' && key !== 'depths'
            ) // .slice(0, 3)
            .map(([hash, value]) => {
                const depth = parseDepth(depths?.[hash])
                return {
                    hash,
                    value: typeof value === 'string' ? value : '',
                    ...(depth !== undefined ? { depth } : {}),
                    ...(lang ? { lang } : {})
                }
            })
            .filter(item => item.value.trim().length > 0)
    }

    const buildMetaPayload = (item: MessageMetaItem) => {
        const parts: string[] = []
        if (typeof item.depth === 'number' && Number.isInteger(item.depth)) {
            parts.push(`<depth:> ${item.depth}`)
        }
        if (item.hash) parts.push(`<hash:> ${item.hash}`)
        if (item.originalTopicName)
            parts.push(`<original_topic_name:> ${item.originalTopicName}`)
        if (item.lang) parts.push(`<lang:> ${item.lang}`)
        return `${item.value} [AUTOMATICALLY ADDED]${parts.join(' ')}[AUTOMATICALLY ADDED]`
    }

    const handleSendMessage = async (
        text: string,
        opts?: { sendOverride?: string }
    ) => {
        if (
            loading ||
            isStreaming ||
            isInputLocked ||
            systemBanner?.type === 'error'
        )
            return

        const msg = normalize(opts?.sendOverride ?? text)
        if (!msg) return

        const userMessage: Message = {
            id: uuidv4(),
            text,
            sender: 'user',
            timestamp: new Date()
        }
        setMessages(prev => [...clearMetaChoices(prev), userMessage])
        setLoading(true)

        const botId = uuidv4()

        try {
            if (sendMessageStream) {
                await sendMessageStream(msg, {
                    onToken: (_delta, full) => {
                        if (typeof full === 'string')
                            upsertBotMessage(botId, full)
                    },
                    onEnd: (full, meta) => {
                        const metaItems = extractMetaItems(meta)
                        if (full || metaItems.length > 0) {
                            upsertBotMessage(
                                botId,
                                full || '',
                                metaItems.length ? metaItems : undefined
                            )
                        }
                    },
                    onError: e => handleStreamError(botId, e)
                })
            } else {
                markActivity()
                clearWarning()
                const response = await (onSendMessage || defaultOnSendMessage)(
                    msg
                )
                upsertBotMessage(botId, response)
                markActivity()
            }
        } catch (e: any) {
            handleStreamError(botId, e)
        } finally {
            setLoading(false)
        }
    }

    const handleMetaClick = (item: MessageMetaItem) => {
        handleSendMessage(item.value, {
            sendOverride: buildMetaPayload(item)
        })
    }

    const showWarn =
        showInlineBanner &&
        systemBanner?.type === 'idle-warning' &&
        !inactivity.cleared
    const showCleared = showInlineBanner && systemBanner?.type === 'cleared'
    const showError = showInlineBanner && systemBanner?.type === 'error'

    return (
        <View
            id="chat-container"
            css={css`
                display: flex;
                flex-direction: column;
                height: 100%;
                width: 100%;
                border-radius: 0 0 0.5rem;
                overflow: hidden;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                background-color: ${th.colors.white};

                @media (max-width: ${Sizes.md}px) {
                    border-radius: 0;
                    box-shadow: none;
                    width: calc(100%);
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 1100;
                    overscroll-behavior: none;
                    -webkit-overflow-scrolling: touch;
                }

                @media (orientation: landscape) and (max-height: ${Sizes.landscapeH}px) {
                    border-radius: 0;
                    box-shadow: none;
                    width: calc(100%);
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 1100;
                    overscroll-behavior: none;
                    -webkit-overflow-scrolling: touch;
                }
            `}
        >
            {showInlineBanner && showWarn && (
                <DeskIdleWarningNotice
                    etaMs={inactivity.msToAutoClear}
                    onClose={dismissWarning}
                />
            )}

            {showInlineBanner && showCleared && (
                <DeskClearedNotice onClose={clearWarning} />
            )}
            {showInlineBanner && showError && (
                <DeskErrorNotice
                    message={
                        systemBanner?.message
                            ? t(systemBanner.message)
                            : t(msg.chatbot.chat.botunavailable.message)
                    }
                    onClose={clearWarning}
                />
            )}
            <ChatMessages
                ref={chatMessagesRef}
                messages={messages}
                loading={loading || isStreaming}
                onMetaClick={handleMetaClick}
            />

            <ChatInput
                onSendMessage={handleSendMessage}
                disabled={
                    loading ||
                    isStreaming ||
                    isInputLocked ||
                    inactivity.cleared ||
                    systemBanner?.type === 'error'
                }
            />
        </View>
    )
}

export default Chat
